import { ReactNode } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';

import { format, parseISO } from 'date-fns';

import { Badge, DEFAULT_DATE_FORMAT } from '@aslanpay/portal-common-lib';

import ProfileField from 'app/components/UI/ProfileField';

import { EarningsStatusDetails } from 'app/constants/earnings';
import { formatCurrency } from 'app/utils/strings';

import { EarningDetailDrawerProps } from './types';

const BASIS_LABEL: Record<string, string> = {
  HOURLY: 'Hourly',
  DAILY: 'Daily',
};

const BASIS_UNIT: Record<string, string> = {
  HOURLY: 'hrs',
  DAILY: 'days',
};

const formatDateValue = (value?: string, withTime = false): string => {
  if (!value) return '—';
  return format(parseISO(value), withTime ? `${DEFAULT_DATE_FORMAT} HH:mm` : DEFAULT_DATE_FORMAT);
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <VStack align="stretch" spacing="10px">
      <Text textStyle="smallSemiBold" color="brand.darkPurple">
        {title}
      </Text>
      {children}
    </VStack>
  );
}

function EarningDetailDrawer({ earning, currency, isOpen, onClose }: EarningDetailDrawerProps) {
  const renderBody = (entry: NonNullable<EarningDetailDrawerProps['earning']>) => {
    const statusDetail = entry.earningsStatus
      ? EarningsStatusDetails[entry.earningsStatus]
      : undefined;

    const hasCalculation = entry.rate != null && entry.unit != null;
    const calculationValue = hasCalculation
      ? `${formatCurrency(entry.rate ?? 0, currency, 'en-GB')} × ${entry.unit}${
          entry.basis ? ` ${BASIS_UNIT[entry.basis]}` : ''
        }${entry.basis ? ` (${BASIS_LABEL[entry.basis]})` : ''}`
      : undefined;

    const drawn =
      entry.remainingEarnings != null
        ? formatCurrency(entry.totalGrossEarnings - entry.remainingEarnings, currency, 'en-GB')
        : undefined;

    const hasProvenance =
      entry.expiredAt || entry.voidedAt || entry.supersededAt || entry.supersededByTimesheetId;

    return (
      <VStack align="stretch" spacing="24px">
        {statusDetail && (
          <VStack align="stretch" spacing="8px">
            <Badge value={statusDetail.label} status={statusDetail.badgeStatus} />
            <Text textStyle="smallRegular" color="brand.grey">
              {statusDetail.description}
            </Text>
          </VStack>
        )}

        <Section title="Amounts">
          <HStack align="flex-start" spacing="20px">
            <ProfileField
              label="Gross earnings"
              value={formatCurrency(entry.totalGrossEarnings, currency, 'en-GB')}
            />
            <ProfileField
              label={
                entry.allowancePercentage != null
                  ? `Net (${entry.allowancePercentage}% allowance)`
                  : 'Net earnings'
              }
              value={formatCurrency(entry.totalNetEarnings, currency, 'en-GB')}
            />
          </HStack>
          {entry.remainingEarnings != null && (
            <HStack align="flex-start" spacing="20px">
              <ProfileField
                label="Remaining"
                value={formatCurrency(entry.remainingEarnings, currency, 'en-GB')}
              />
              <ProfileField label="Drawn" value={drawn} />
            </HStack>
          )}
        </Section>

        {(calculationValue || entry.approvedEarnings != null) && (
          <Section title="Calculation">
            {calculationValue && <ProfileField label="Rate × unit" value={calculationValue} />}
            {entry.approvedEarnings != null && (
              <ProfileField
                label="Approved amount"
                value={formatCurrency(entry.approvedEarnings, currency, 'en-GB')}
              />
            )}
          </Section>
        )}

        <Section title="Dates">
          <HStack align="flex-start" spacing="20px">
            <ProfileField label="Earning date" value={formatDateValue(entry.earningDate)} />
            <ProfileField label="Uploaded at" value={formatDateValue(entry.uploadedAt, true)} />
          </HStack>
          <ProfileField
            label="Pay cycle"
            value={`${formatDateValue(entry.earningsStartDate)} - ${formatDateValue(
              entry.earningsEndDate
            )}`}
          />
        </Section>

        {hasProvenance && (
          <Section title="History">
            {entry.expiredAt && (
              <ProfileField label="Expired at" value={formatDateValue(entry.expiredAt, true)} />
            )}
            {entry.voidedAt && (
              <ProfileField label="Voided at" value={formatDateValue(entry.voidedAt, true)} />
            )}
            {entry.supersededAt && (
              <ProfileField
                label="Superseded at"
                value={formatDateValue(entry.supersededAt, true)}
              />
            )}
            {entry.supersededByTimesheetId && (
              <ProfileField
                label="Superseded by timesheet"
                value={entry.supersededByTimesheetId}
                copyToClipboard
              />
            )}
            {entry.timesheetId && (
              <ProfileField label="Source timesheet" value={entry.timesheetId} copyToClipboard />
            )}
          </Section>
        )}
      </VStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Earning detail</DrawerHeader>
        <DrawerBody>{earning && renderBody(earning)}</DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default EarningDetailDrawer;
